import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AlertTriangle, ChevronDown, RefreshCw, Server } from 'lucide-react';
import platformMcpService from '../../services/platformMcpService';
import { describeApiError } from '../../utils/apiError';
import { formatWhen } from '../shared/RunDetail/runDetailFormat';
import CopyableCode from '../shared/CopyableCode';
import InfoTip from '../shared/InfoTip';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import styles from './PlatformAdmin.module.scss';
import mcpStyles from './PlatformMcpPanel.module.scss';

const RATE_LIMIT_MAX = 100000;

/** Status chip: On / Off / Draft / Not set up (spec §4.1). */
const statusChip = (s) => {
  if (!s?.exists) return { label: 'Not set up', tone: 'neutral' };
  if (s.published) return { label: 'On', tone: 'success' };
  if (s.status === 'disabled') return { label: 'Off', tone: 'neutral' };
  if (s.status === 'draft') return { label: 'Draft', tone: 'info' };
  return { label: s.status || 'Unknown', tone: 'neutral' };
};

const listOrEmpty = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

/** Outcome of the last Set up / Update, kept until the next one. */
const SetupResult = ({ result, onDismiss }) => {
  const [showDetails, setShowDetails] = useState(false);
  const added = listOrEmpty(result.added);
  const disabled = listOrEmpty(result.disabled_by_sync);
  const blocking = listOrEmpty(result.lint?.blocking);
  const warnings = listOrEmpty(result.lint?.warnings);
  const notes = listOrEmpty(result.import_notes);
  const hasDetails = warnings.length > 0 || notes.length > 0;

  return (
    <Box className={mcpStyles.resultBox}>
      {result.notice && <Alert severity="warning">{result.notice}</Alert>}
      {blocking.length > 0 && (
        <Alert severity="error">
          <strong>{PLATFORM_MCP_COPY.blockingTitle}</strong>
          <ul className={mcpStyles.list}>
            {blocking.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </Alert>
      )}
      {added.length > 0 && <Alert severity="success">{PLATFORM_MCP_COPY.newTools(added)}</Alert>}
      {disabled.length > 0 && <Alert severity="info">{PLATFORM_MCP_COPY.disabledBySync(disabled)}</Alert>}
      <Box className={mcpStyles.resultFooter}>
        {hasDetails && (
          <Button
            size="small"
            className={styles.subtleBtn}
            onClick={() => setShowDetails((v) => !v)}
            endIcon={
              <ChevronDown
                size={15}
                style={{ transform: showDetails ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            }
          >
            Details ({warnings.length + notes.length})
          </Button>
        )}
        <Button size="small" className={styles.subtleBtn} onClick={onDismiss}>
          Dismiss
        </Button>
      </Box>
      {hasDetails && (
        <Collapse in={showDetails} unmountOnExit>
          <ul className={`${mcpStyles.list} ${mcpStyles.details}`}>
            {notes.map((line, i) => (
              <li key={`n${i}`}>{line}</li>
            ))}
            {warnings.map((line, i) => (
              <li key={`w${i}`}>{line}</li>
            ))}
          </ul>
        </Collapse>
      )}
    </Box>
  );
};

/**
 * Quadroid server status, Set up / Update, on/off and rate limit (spec §4.1–4.3).
 * The parent owns `status` so the Tools section can refresh the tool count.
 */
const PlatformMcpServerSection = ({ status, loading, error, onReload, notify }) => {
  const [settingUp, setSettingUp] = useState(false);
  const [setupResult, setSetupResult] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  const exists = !!status?.exists;
  const chip = statusChip(status);

  useEffect(() => {
    setRateInput(String(status?.rate_limit_per_min ?? 0));
  }, [status?.rate_limit_per_min]);

  const handleSetup = async () => {
    setSettingUp(true);
    try {
      const res = await platformMcpService.setup();
      setSetupResult(res || null);
      notify(res?.created ? PLATFORM_MCP_COPY.serverCreated : PLATFORM_MCP_COPY.serverUpdated, 'success');
    } catch (err) {
      // 409 = another setup is running; 400 = a server setting is missing.
      notify(describeApiError(err, "Couldn't set up the Quadroid server."), 'error');
    } finally {
      setSettingUp(false);
      onReload();
    }
  };

  const setPublished = async (published) => {
    setSwitching(true);
    try {
      await platformMcpService.update({ published });
      notify(published ? 'Quadroid server switched on.' : 'Quadroid server switched off.', 'success');
    } catch (err) {
      notify(describeApiError(err, "Couldn't change the server status."), 'error');
    } finally {
      setSwitching(false);
      setConfirmOff(false);
      onReload();
    }
  };

  const rateValue = Number(rateInput);
  const rateValid = rateInput !== '' && Number.isInteger(rateValue) && rateValue >= 0 && rateValue <= RATE_LIMIT_MAX;
  const rateDirty = rateValid && rateValue !== (status?.rate_limit_per_min ?? 0);

  const saveRate = async () => {
    if (!rateValid) return;
    setSavingRate(true);
    try {
      await platformMcpService.update({ rate_limit_per_min: rateValue });
      notify(
        rateValue === 0 ? `Rate limit set to ${PLATFORM_MCP_COPY.platformDefault.toLowerCase()}.` : 'Rate limit saved.',
        'success'
      );
    } catch (err) {
      notify(describeApiError(err, "Couldn't save the rate limit."), 'error');
    } finally {
      setSavingRate(false);
      onReload();
    }
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !status) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onReload}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box className={mcpStyles.section}>
      {exists && status?.update_available && (
        <Alert severity="warning">{PLATFORM_MCP_COPY.updateBanner}</Alert>
      )}

      <Box className={mcpStyles.card}>
        <Box className={mcpStyles.cardHead}>
          <Box className={mcpStyles.cardTitleRow}>
            <Server size={20} />
            <Typography className={mcpStyles.cardTitle}>Quadroid server</Typography>
            <span className={`${styles.effBadge} ${styles[chip.tone]}`}>{chip.label}</span>
          </Box>
          <Box className={styles.filterGroup}>
            <Tooltip title="Refresh" arrow>
              <span>
                <Button size="small" className={styles.subtleBtn} onClick={onReload} disabled={loading}>
                  <RefreshCw size={16} />
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={PLATFORM_MCP_COPY.setupTip} arrow>
              <span>
                <Button
                  variant={!exists || status?.update_available ? 'contained' : 'outlined'}
                  className={!exists || status?.update_available ? styles.primaryBtn : styles.outlineBtn}
                  onClick={handleSetup}
                  disabled={settingUp}
                  startIcon={settingUp ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {settingUp ? 'Working…' : exists ? 'Update' : 'Set up'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {exists ? (
          <Box className={mcpStyles.facts}>
            {status.url && (
              <Box className={mcpStyles.factWide}>
                <CopyableCode
                  label="SERVER LINK"
                  tooltip={PLATFORM_MCP_COPY.serverLinkTip}
                  value={status.url}
                  ariaLabel="Copy server link"
                />
              </Box>
            )}
            <Box className={mcpStyles.fact}>
              <span className={mcpStyles.factLabel}>Tools</span>
              <span>
                {status.tools_enabled ?? 0} of {status.tools_total ?? 0} tools on
              </span>
            </Box>
            <Box className={mcpStyles.fact}>
              <span className={mcpStyles.factLabel}>Rate limit</span>
              <span>
                {status.rate_limit_per_min
                  ? `${status.rate_limit_per_min} calls / min per key`
                  : PLATFORM_MCP_COPY.platformDefault}
              </span>
            </Box>
            {status.config_version != null && (
              <Box className={mcpStyles.fact}>
                <span className={mcpStyles.factLabel}>Config version</span>
                <span>{status.config_version}</span>
              </Box>
            )}
            {status.published_at && (
              <Box className={mcpStyles.fact}>
                <span className={mcpStyles.factLabel}>Published</span>
                <span>{formatWhen(status.published_at)}</span>
              </Box>
            )}
            {status.updated_at && (
              <Box className={mcpStyles.fact}>
                <span className={mcpStyles.factLabel}>Updated</span>
                <span>{formatWhen(status.updated_at)}</span>
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" className={styles.muted}>
            The Quadroid server hasn't been set up yet. Click <strong>Set up</strong> to create it.
          </Typography>
        )}

        {setupResult && <SetupResult result={setupResult} onDismiss={() => setSetupResult(null)} />}
      </Box>

      {exists && (
        <Box className={mcpStyles.card}>
          <Typography className={mcpStyles.cardTitle}>Access</Typography>

          <Box className={mcpStyles.controlRow}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!status.published}
                  disabled={switching}
                  onChange={(e) => (e.target.checked ? setPublished(true) : setConfirmOff(true))}
                />
              }
              label={status.published ? 'Server is on' : 'Server is off'}
            />
            <InfoTip title={PLATFORM_MCP_COPY.onOffTip} />
            {switching && <CircularProgress size={16} />}
          </Box>

          <Box className={mcpStyles.controlRow}>
            <TextField
              size="small"
              type="number"
              label="Rate limit (calls / min per key)"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && rateDirty && !savingRate && saveRate()}
              inputProps={{ min: 0, max: RATE_LIMIT_MAX, step: 1 }}
              error={rateInput !== '' && !rateValid}
              helperText={
                rateInput !== '' && !rateValid
                  ? `A whole number from 0 to ${RATE_LIMIT_MAX}.`
                  : rateValue === 0
                    ? PLATFORM_MCP_COPY.platformDefault
                    : ' '
              }
              sx={{ width: 260 }}
            />
            <InfoTip title={PLATFORM_MCP_COPY.rateLimitTip} />
            <Button
              variant="outlined"
              className={styles.outlineBtn}
              onClick={saveRate}
              disabled={!rateDirty || savingRate}
              startIcon={savingRate ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ alignSelf: 'flex-start' }}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={confirmOff}
        onClose={switching ? undefined : () => setConfirmOff(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}>
            <AlertTriangle size={28} />
          </Box>
          <Typography className={styles.deleteHeading}>Switch the server off?</Typography>
          <Typography className={styles.deleteText}>{PLATFORM_MCP_COPY.switchOffConfirm}</Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button className={styles.cancelButton} onClick={() => setConfirmOff(false)} disabled={switching}>
            Cancel
          </Button>
          <Button
            className={styles.dangerButton}
            onClick={() => setPublished(false)}
            disabled={switching}
            startIcon={switching ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Switch off
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformMcpServerSection;
